import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import { ApiError } from "./types/common-types";
import {
  TopupcardProviderState,
  TopupcardProviderResponse,
  TopupcardProvider,
} from "./types/topup-types";
import { PaginationReturn } from "./types/types";

const initialState: TopupcardProviderState = {
  topupcardProviders: [],
  topupcardProvider: null,
  selectedTopupcardProvider: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// CREATE TopupProvider
export const createTopupcardProvider = createAsyncThunk<
  TopupcardProviderResponse,
  FormData | Omit<TopupcardProvider, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("topupcardProviders/create", async (topupProviderData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = topupProviderData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/top-ups-providers`,
      topupProviderData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      }
    );
    return {
      message: res.data.message,
      dataProvider: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create top up provider" }
    );
  }
});

// READ (fetch all) TopupProviders
export const fetchTopupcardProviders = createAsyncThunk<
  TopupcardProviderResponse & PaginationReturn,
  void | { url: string },
  { rejectValue: ApiError; state: RootState }
>("topupcardProviders/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url: string | undefined = urlFilters?.url;
    const apiUrl = url || `${API_URL}/top-ups-providers`;
    const res = await axios.get(apiUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      message: res.data.data.message,
      topupcardProviders: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch top up providers" }
    );
  }
});

// READ (fetch one) DataProvider by ID
export const fetchTopupcardProviderById = createAsyncThunk<
  TopupcardProvider,
  number,
  { rejectValue: ApiError; state: RootState }
>("topupcardProviders/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/top-ups-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch top up provider" }
    );
  }
});

// UPDATE DataProvider
export const updateTopupcardProvider = createAsyncThunk<
  TopupcardProvider,
  { id: number; data: Partial<TopupcardProvider> },
  { rejectValue: ApiError; state: RootState }
>("topupcardProviders/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/top-ups-providers/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      }
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update top up provider" }
    );
  }
});

// DELETE DataProvider
export const deleteTopupcardProvider = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("topupcardProviders/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/top-ups-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete top up provider" }
    );
  }
});

// Slice
const topupcardProviderSlice = createSlice({
  name: "topupcardProviders",
  initialState,
  reducers: {
    resetTopupcardProviderState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedTopupcardProvider = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createTopupcardProvider.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createTopupcardProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.topupcardProvider) {
          state.topupcardProviders.unshift(action.payload.topupcardProvider);
        }
      })
      .addCase(createTopupcardProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create top up provider";
      })

      // Fetch All
      .addCase(fetchTopupcardProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopupcardProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.topupcardProviders = action.payload.topupcardProviders || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchTopupcardProviders.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch top up providers";
      })

      // Fetch One
      .addCase(fetchTopupcardProviderById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedTopupcardProvider = null;
      })
      .addCase(fetchTopupcardProviderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTopupcardProvider = action.payload;
      })
      .addCase(fetchTopupcardProviderById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch top up provider";
      })

      // Update
      .addCase(updateTopupcardProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateTopupcardProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.topupcardProviders = state.topupcardProviders.map((provider) =>
          provider.id === action.payload.id ? action.payload : provider
        );
        if (state.selectedTopupcardProvider?.id === action.payload.id) {
          state.selectedTopupcardProvider = action.payload;
        }
      })
      .addCase(updateTopupcardProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update top up provider";
      })

      // Delete
      .addCase(deleteTopupcardProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteTopupcardProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.topupcardProviders = state.topupcardProviders.filter(
          (provider) => provider.id !== action.payload
        );
      })
      .addCase(deleteTopupcardProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete top up provider";
      });
  },
});

export const { resetTopupcardProviderState } = topupcardProviderSlice.actions;
export default topupcardProviderSlice.reducer;
